import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useRef, useEffect, useState, useCallback } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { Columns2, X } from 'lucide-react'
import { TranslationKey, useTar } from '@/modules/i18n'

// Shared tab styles - exported for use by AddTabButton
export const getTabClassName = (isActive: boolean, isDragging: boolean, isThin: boolean, className?: string) =>
    cn(
        'relative h-8 cursor-pointer select-none',
        isThin ? 'ps-3 pe-2' : 'px-5',
        'flex items-center justify-center',
        'before:border-[1px] before:border-border before:border-solid before:border-muted/60 before:border-b-0',
        'text-sm font-medium transition-colors outline-none',
        'before:absolute before:inset-0 before:rounded-t-md',
        'before:[transform:perspective(8px)_rotateX(2deg)]',
        'before:origin-bottom before:transition-colors',
        isActive
            ? 'text-foreground before:bg-primary/75'
            : 'text-muted-foreground hover:text-foreground before:bg-card',
        isDragging && 'cursor-grabbing',
        className
    )

export interface WorkspaceTabProps {
    sortableId: string
    label: TranslationKey
    tabType: ChallengeService.WorkspaceTabTypeEnum
    index: number
    isActive: boolean
    isDragActive: boolean
    className?: string
    onActivateTab: () => void
    onRemoveTab?: () => void
    /** No-arg callback (parent binds tab context). Tooltip: 'common.moveInSplitView' or 'common.moveToOtherView' */
    onOpenInSplitView?: () => void
    splitViewTooltipKey?: TranslationKey
    tabRef?: (el: HTMLDivElement | null) => void
}

export const WorkspaceTab = ({
    sortableId,
    label,
    tabType,
    index,
    isActive,
    isDragActive,
    className,
    onActivateTab,
    onRemoveTab,
    onOpenInSplitView,
    splitViewTooltipKey = 'common.openInSplitView',
    tabRef,
}: WorkspaceTabProps) => {
    const wasDragActiveRef = useRef(false)
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [showCloseBox, setShowCloseBox] = useState(false)
    const { t } = useTar()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useSortable({
        id: sortableId,
    })

    const justDropped = wasDragActiveRef.current && !isDragActive

    useEffect(() => {
        wasDragActiveRef.current = isDragActive
    }, [isDragActive])

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
        }
    }, [])

    const showActionsBox = Boolean(onRemoveTab || onOpenInSplitView)
    const handleMouseEnter = useCallback(() => {
        if (!showActionsBox) return
        hoverTimerRef.current = setTimeout(() => setShowCloseBox(true), 160)
    }, [showActionsBox])

    const handleMouseLeave = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current)
            hoverTimerRef.current = null
        }
        setShowCloseBox(false)
    }, [])

    const style = {
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition: justDropped ? 'none' : undefined,
        zIndex: isDragging ? 100 : 10 - index + (isActive ? 10 : 0),
        opacity: isDragging ? 0 : 1,
    }

    return (
        <div
            ref={(el) => {
                setNodeRef(el)
                tabRef?.(el)
            }}
            style={style}
            {...attributes}
            {...listeners}
            role="tab"
            tabIndex={0}
            aria-selected={isActive}
            aria-current={isActive ? 'page' : undefined}
            className={cn('group', getTabClassName(isActive, isDragging, false, className))}
            onClick={onActivateTab}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onAuxClick={(e) => {
                if (e.button === 1 && onRemoveTab) {
                    e.preventDefault()
                    onRemoveTab()
                }
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onActivateTab()
                }
            }}
        >
            {showActionsBox && onRemoveTab && (
                <TooltipProvider>
                    <div
                        className={cn(
                            'absolute bottom-full left-1/2 -translate-x-1/2 mb-0 z-20',
                            'bg-card border border-border/60 rounded-sm shadow-sm',
                            'flex items-stretch',
                            'transition-all duration-200 ease-out',
                            showCloseBox
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-1.5 pointer-events-none'
                        )}
                    >
                        <Tooltip disableHoverableContent>
                            <TooltipTrigger asChild>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={t('common.close')}
                                    className={cn(
                                        'flex items-center justify-center p-0.5 hover:bg-destructive/15 hover:text-destructive transition-colors',
                                        onOpenInSplitView ? 'rounded-r-sm' : 'rounded-sm'
                                    )}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onRemoveTab()
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            onRemoveTab()
                                        }
                                    }}
                                >
                                    <X className="size-3" />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                {t('common.close')}
                            </TooltipContent>
                        </Tooltip>
                        {onOpenInSplitView && (
                            <Tooltip disableHoverableContent>
                                <TooltipTrigger asChild>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label={t(splitViewTooltipKey)}
                                        className={cn(
                                            'flex items-center justify-center p-0.5 hover:bg-muted hover:text-foreground transition-colors',
                                            'rounded-l-sm border-r border-border/40'
                                        )}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onOpenInSplitView()
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.stopPropagation()
                                                e.preventDefault()
                                                onOpenInSplitView()
                                            }
                                        }}
                                    >
                                        <Columns2 className="size-3" />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    {t(splitViewTooltipKey)}
                                </TooltipContent>
                            </Tooltip>
                        )}

                    </div>
                </TooltipProvider>
            )}
            <span className="relative z-10 pt-1 whitespace-nowrap">
                {t(label)}
            </span>
        </div>
    )
}

// Static overlay component for DragOverlay - not affected by layout shifts
export interface WorkspaceTabOverlayProps {
    label: TranslationKey
    isActive: boolean
    className?: string
}

export const WorkspaceTabOverlay = ({
    label,
    isActive,
    className,
}: WorkspaceTabOverlayProps) => {
    const { t } = useTar()
    return (
        <div
            className={getTabClassName(isActive, true, false, className)}
            style={{ zIndex: 100 }}
        >
            <span className="relative z-10 pt-1 whitespace-nowrap">{t(label)}</span>
        </div>
    )
}
